import { CashWorkflowPanel } from './cash/CashWorkflowPanel';
import { AdminPage, AdminPageHeader } from '../../components/layout/AdminPage';
/**
 * @page ExpenseEntryPage
 * @description 館務費用收支管理（UI-4-2）
 * 當日收支登錄、刪除、查詢
 */

import { useState } from 'react';
import { isAxiosError } from 'axios';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api';
import type { DailySessionDto, TransactionDto, ExpenseCategoryDto } from '@pokerroom/types';

export default function ExpenseEntryPage() {
  const { t } = useTranslation('accounting');
  const { t: tc } = useTranslation('common');
  const qc = useQueryClient();

  const [form, setForm] = useState({
    type: 'EXPENSE' as 'INCOME' | 'EXPENSE',
    amount: '',
    description: '',
    categoryId: '',
  });

  const [error, setError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const validAmount = /^\d{1,10}(?:\.\d{1,2})?$/.test(form.amount) && /[1-9]/.test(form.amount);
  const showError = (err: unknown) => {
    const message: unknown = isAxiosError(err) ? err.response?.data?.message : undefined;
    setError(
      typeof message === 'string'
        ? message
        : Array.isArray(message) && message.every((item) => typeof item === 'string')
          ? message.join('; ')
          : t('requestFailed'),
    );
  };

  const { data: session } = useQuery<DailySessionDto | null>({
    queryKey: ['accounting-active-session'],
    queryFn: () => api.get<DailySessionDto | null>('/accounting/sessions/active'),
  });

  const { data: categories = [] } = useQuery<ExpenseCategoryDto[]>({
    queryKey: ['expense-categories'],
    queryFn: () => api.get<ExpenseCategoryDto[]>('/accounting/categories'),
  });

  const { data: transactions = [], isLoading } = useQuery<TransactionDto[]>({
    queryKey: ['transactions', session?.id],
    queryFn: () => api.get<TransactionDto[]>(`/accounting/transactions?sessionId=${session!.id}`),
    enabled: !!session?.id,
  });

  const createMutation = useMutation({
    mutationFn: (dto: object) => api.post('/accounting/transactions', dto),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['transactions'] });
      qc.invalidateQueries({ queryKey: ['accounting-summary'] });
      setError('');
      setForm({ type: 'EXPENSE', amount: '', description: '', categoryId: '' });
    },
    onError: showError,
  });

  const deleteMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      api.delete(`/accounting/transactions/${id}`, { data: { reason } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['transactions'] });
      qc.invalidateQueries({ queryKey: ['accounting-summary'] });
      setDeleteTarget(null);
      setReason('');
      setError('');
    },
    onError: showError,
  });

  if (session?.cashMode === 'LOCATION_V1')
    return (
      <AdminPage
        className="accounting-page"
        contentClassName="accounting-form-body"
        header={<AdminPageHeader title={t('pageTitles.expenses')} />}
      >
        <CashWorkflowPanel surface="registration" accountingLayout sessionId={session.id} />
      </AdminPage>
    );

  if (!session) {
    return (
      <AdminPage
        className="accounting-page"
        contentClassName="accounting-form-body"
        header={<AdminPageHeader title={t('pageTitles.expenses')} />}
      >
        <div className="admin-panel space-y-3">
          <h2 className="text-xl font-bold text-base-content mb-2">{t('notOpened')}</h2>
          <p className="text-base-content/60 mb-6">{t('notOpenedDesc')}</p>
          <a
            href="/accounting/open"
            className="px-6 py-2 rounded-md bg-primary text-primary-content"
          >
            {t('goToOpen')}
          </a>
        </div>
      </AdminPage>
    );
  }

  return (
    <AdminPage
      className="accounting-page"
      contentClassName="accounting-form-body"
      header={<AdminPageHeader title={t('pageTitles.expenses')} />}
    >
      <p className="text-sm text-base-content/60 mb-6">{session.sessionDate.slice(0, 10)} </p>

      {error && (
        <p role="alert" className="text-error mb-4">
          {error}
        </p>
      )}

      {deleteTarget && (
        <section
          role="dialog"
          aria-labelledby="delete-transaction-title"
          className="bg-base-200 border border-base-300 rounded-xl p-5 mb-6"
        >
          <h2 id="delete-transaction-title" className="font-semibold mb-3">
            {t('confirmDeleteTransaction')}
          </h2>
          <label htmlFor="delete-transaction-reason">{t('deleteReason')}</label>
          <textarea
            id="delete-transaction-reason"
            maxLength={255}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            disabled={deleteMutation.isPending}
            className="block w-full border border-base-300 rounded-md p-2 my-3"
          />
          <p className="text-sm mb-3">{t('deleteReasonHelp')}</p>
          <button
            onClick={() => deleteMutation.mutate({ id: deleteTarget, reason: reason.trim() })}
            disabled={!reason.trim() || reason.trim().length > 255 || deleteMutation.isPending}
            className="px-4 py-2 bg-error text-error-content rounded-md disabled:opacity-50"
          >
            {tc('delete')}
          </button>
          <button
            onClick={() => {
              setDeleteTarget(null);
              setError('');
            }}
            disabled={deleteMutation.isPending}
            className="px-4 py-2 ml-3"
          >
            {tc('cancel')}
          </button>
        </section>
      )}
      <div className="grid grid-cols-5 gap-6">
        {/* 新增收支表單 */}
        <div className="col-span-2 bg-base-200 border border-base-300 rounded-xl p-5">
          <h2 className="font-semibold text-base-content mb-4">{t('addTransaction')}</h2>

          {/* 收入/支出切換 */}
          <div className="flex rounded-lg overflow-hidden border border-base-300 mb-4">
            {(['INCOME', 'EXPENSE'] as const).map((type) => (
              <button
                key={type}
                onClick={() => setForm((p) => ({ ...p, type }))}
                className={`flex-1 py-2 text-sm font-medium transition-colors
                  ${
                    form.type === type
                      ? type === 'INCOME'
                        ? 'bg-emerald-600 text-white'
                        : 'bg-red-600 text-white'
                      : 'text-base-content/60 hover:bg-base-300'
                  }`}
              >
                {t(type.toLowerCase() as 'income' | 'expense')}
              </button>
            ))}
          </div>

          <div className="space-y-3">
            <div>
              <label className="text-xs text-base-content/60 block mb-1">
                {t('amount')} (NT$) *
              </label>
              <input
                type="number"
                id="transaction-amount"
                aria-label={t('amount')}
                min="0.01"
                max="9999999999.99"
                step="0.01"
                value={form.amount}
                onChange={(e) => setForm((p) => ({ ...p, amount: e.target.value }))}
                className="w-full px-3 py-2 rounded-md border border-base-300 bg-base-200 text-base-content
                           focus:outline-hidden focus:ring-2 focus:ring-primary font-mono"
              />
            </div>
            <div>
              <label className="text-xs text-base-content/60 block mb-1">
                {t('description')} *
              </label>
              <input
                type="text"
                aria-label={t('description')}
                maxLength={255}
                value={form.description}
                onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                className="w-full px-3 py-2 rounded-md border border-base-300 bg-base-200 text-base-content
                           focus:outline-hidden focus:ring-2 focus:ring-primary"
              />
            </div>
            {form.type === 'EXPENSE' && (
              <div>
                <label className="text-xs text-base-content/60 block mb-1">{t('category')}</label>
                <select
                  value={form.categoryId}
                  onChange={(e) => setForm((p) => ({ ...p, categoryId: e.target.value }))}
                  className="w-full px-3 py-2 rounded-md border border-base-300 bg-base-200 text-base-content
                             focus:outline-hidden focus:ring-2 focus:ring-primary"
                >
                  <option value="">{t('noCategory')}</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <button
              onClick={() =>
                createMutation.mutate({
                  sessionId: session.id,
                  type: form.type,
                  amount: form.amount,
                  description: form.description.trim(),
                  categoryId: form.categoryId || undefined,
                })
              }
              disabled={
                !validAmount ||
                !form.description.trim() ||
                form.description.trim().length > 255 ||
                createMutation.isPending
              }
              className="w-full py-2.5 rounded-md bg-primary text-primary-content text-sm font-medium
                         disabled:opacity-50 hover:bg-primary/90"
            >
              {createMutation.isPending ? tc('loading') : tc('create')}
            </button>
          </div>
        </div>

        {/* 收支清單 */}
        <div className="col-span-3">
          <h2 className="font-semibold text-base-content mb-3">{t('todayTransactions')}</h2>
          <div className="bg-base-200 border border-base-300 rounded-xl overflow-hidden">
            {isLoading ? (
              <div className="text-center py-8 text-base-content/40">{tc('loading')}</div>
            ) : transactions.length === 0 ? (
              <div className="text-center py-8 text-base-content/40">{tc('noData')}</div>
            ) : (
              <div className="divide-y divide-base-300">
                {transactions.map((tx) => (
                  <div key={tx.id} className="px-4 py-3 flex items-center gap-3">
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full font-medium
                      ${tx.type === 'INCOME' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}
                    >
                      {t(tx.type.toLowerCase() as 'income' | 'expense')}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-base-content truncate">{tx.description}</p>
                      <p className="text-xs">{t(`cashFlow_${tx.cashFlow ?? 'CASH'}`)}</p>
                      {tx.categoryName && (
                        <p className="text-xs text-base-content/50">{tx.categoryName}</p>
                      )}
                    </div>
                    <span
                      className={`font-mono text-sm font-medium
                      ${tx.type === 'INCOME' ? 'text-emerald-600' : 'text-red-600'}`}
                    >
                      {tx.type === 'INCOME' ? '+' : '-'}NT$
                      {(parseFloat(tx.amount) || 0).toLocaleString()}
                    </span>
                    <button
                      onClick={() => {
                        setDeleteTarget(tx.id);
                        setReason('');
                        setError('');
                      }}
                      aria-label={`${tc('delete')}: ${tx.description}`}
                      disabled={deleteMutation.isPending || tx.receiptLinked}
                      className="text-base-content/30 hover:text-red-500 text-xs ml-1"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </AdminPage>
  );
}
